import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LoginIcon from '@mui/icons-material/Login';
import GoogleIcon from '@mui/icons-material/Google';
import { friendlyAuthError, useCustomer } from '../../contexts/CustomerContext';

// All sign-in flows funnel through this one dialog so the entry points
// (app bar, checkout banner, account page guard) don't each have to
// implement Google/email/reset themselves. A single instance is mounted
// at the app root by SignInDialogProvider; consumers open it via
// useSignInDialog().openSignIn().

type Mode = 'signIn' | 'signUp' | 'reset';

type SignInDialogCtx = {
  open: boolean;
  openSignIn: () => void;
  closeSignIn: () => void;
};

const SignInDialogContext = createContext<SignInDialogCtx>({
  open: false,
  openSignIn: () => {},
  closeSignIn: () => {},
});

export const useSignInDialog = () => useContext(SignInDialogContext);

export const SignInDialogProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const value = useMemo<SignInDialogCtx>(
    () => ({
      open,
      openSignIn: () => setOpen(true),
      closeSignIn: () => setOpen(false),
    }),
    [open],
  );
  return (
    <SignInDialogContext.Provider value={value}>
      {children}
      <SignInDialog open={open} onClose={() => setOpen(false)} />
    </SignInDialogContext.Provider>
  );
};

type Props = {
  open: boolean;
  onClose: () => void;
};

const SignInDialog: React.FC<Props> = ({ open, onClose }) => {
  const {
    user,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    sendPasswordReset,
  } = useCustomer();

  const [mode, setMode] = useState<Mode>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  // Reset form state any time the dialog reopens or the mode flips —
  // stale errors/info from a prior session shouldn't bleed in.
  useEffect(() => {
    if (open) {
      setError('');
      setInfo('');
      setPassword('');
    }
  }, [open, mode]);

  // Close automatically once Firebase reports a signed-in user. This
  // covers all three flows (Google popup, email sign-in, sign-up) and
  // avoids any close-before-onIdTokenChanged race.
  useEffect(() => {
    if (open && user) {
      onClose();
      setMode('signIn');
    }
  }, [open, user, onClose]);

  const wrap = useCallback(
    async (fn: () => Promise<void>) => {
      setBusy(true);
      setError('');
      setInfo('');
      try {
        await fn();
      } catch (err: any) {
        const msg = friendlyAuthError(err);
        if (msg) setError(msg);
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const onGoogle = () => wrap(() => signInWithGoogle());

  const onEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'reset') {
      if (!email.trim()) {
        setError('Enter your email address to reset the password.');
        return;
      }
      wrap(async () => {
        await sendPasswordReset(email.trim());
        setInfo('Reset link sent. Check your inbox.');
      });
      return;
    }
    if (!email.trim() || !password) {
      setError('Enter both email and password.');
      return;
    }
    const action = mode === 'signUp' ? signUpWithEmail : signInWithEmail;
    wrap(() => action(email.trim(), password));
  };

  const title =
    mode === 'signIn' ? 'Sign in' : mode === 'signUp' ? 'Create account' : 'Reset password';
  const primaryLabel =
    mode === 'signIn' ? 'Sign in' : mode === 'signUp' ? 'Create account' : 'Send reset link';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        {title}
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {mode !== 'reset' && (
            <>
              <Button
                fullWidth
                variant="outlined"
                onClick={onGoogle}
                disabled={busy}
                startIcon={busy ? <CircularProgress size={16} /> : <GoogleIcon />}
                sx={{ textTransform: 'none' }}
              >
                Continue with Google
              </Button>
              <Divider>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  or
                </Typography>
              </Divider>
            </>
          )}

          <Box component="form" onSubmit={onEmailSubmit}>
            <Stack spacing={2}>
              <TextField
                type="email"
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                fullWidth
                size="small"
                required
                disabled={busy}
              />
              {mode !== 'reset' && (
                <TextField
                  type="password"
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === 'signUp' ? 'new-password' : 'current-password'}
                  fullWidth
                  size="small"
                  required
                  disabled={busy}
                  helperText={mode === 'signUp' ? 'At least 6 characters.' : undefined}
                />
              )}

              {error && <Alert severity="error">{error}</Alert>}
              {info && <Alert severity="success">{info}</Alert>}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={busy}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <LoginIcon />}
                sx={{ textTransform: 'none' }}
              >
                {primaryLabel}
              </Button>
            </Stack>
          </Box>

          <Stack direction="row" justifyContent="space-between" sx={{ pt: 1 }}>
            {mode === 'signIn' && (
              <>
                <MuiLink
                  component="button"
                  type="button"
                  variant="body2"
                  underline="hover"
                  onClick={() => setMode('reset')}
                >
                  Forgot password?
                </MuiLink>
                <MuiLink
                  component="button"
                  type="button"
                  variant="body2"
                  underline="hover"
                  onClick={() => setMode('signUp')}
                >
                  Create account
                </MuiLink>
              </>
            )}
            {mode === 'signUp' && (
              <MuiLink
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={() => setMode('signIn')}
              >
                Already have an account? Sign in
              </MuiLink>
            )}
            {mode === 'reset' && (
              <MuiLink
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={() => setMode('signIn')}
              >
                Back to sign in
              </MuiLink>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default SignInDialog;
